/**
 * Tenant · AI Legal Search
 *
 * Free-text precedent search powered by eCourts + AI-generated IRAC headnotes.
 *
 * Tabs:
 *   1) Search      — query, results, generate headnote
 *   2) My Library  — saved headnotes by the current user
 *   3) Firm Library— headnotes shared by colleagues
 *   4) Settings    — default visibility & save-to-matter
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Sparkles, BookOpen, Settings as Cog, Loader2,
    Trash2, Globe2, Lock, Hash, CheckCircle2, Paperclip, SlidersHorizontal, Download, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Field, Input, Select, GoldButton, EmptyState } from "@/components/Ui";
import Modal from "@/components/Ui";
import PoweredByIKanoonBadge from "@/components/PoweredByIKanoonBadge";

const SCOPE_OPTIONS = [
    { value: "any",      label: "Any field" },
    { value: "party",    label: "Party name only" },
    { value: "advocate", label: "Advocate name only" },
    { value: "judge",    label: "Judge name only" },
];

const VIS_OPTIONS = [
    { value: "private", label: "Private (only me)" },
    { value: "firm",    label: "Share with firm" },
];

const ATTACH_OPTIONS = [
    { value: "off",    label: "Don't suggest attaching to a matter" },
    { value: "manual", label: "Ask me each time" },
    { value: "auto",   label: "Auto-suggest the most recent matter" },
];

export default function LegalSearch() {
    const [tab, setTab] = useState("search");
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        api.get("/legal-search/settings")
            .then((r) => setSettings(r.data))
            .catch(() => setSettings({ default_visibility: "private",
                                       default_save_to_matter: "manual" }));
    }, []);

    return (
        <div className="space-y-6" data-testid="legal-search-page">
            <PageHeader
                title="AI Legal Search"
                subtitle="Precedent search across Indian courts + AI-generated IRAC headnotes."
            />

            <div className="flex flex-wrap gap-2 border-b border-zinc-200 dark:border-zinc-800">
                {[
                    { key: "search",  label: "Search",       icon: Search },
                    { key: "mine",    label: "My Library",   icon: BookOpen },
                    { key: "firm",    label: "Firm Library", icon: Globe2 },
                    { key: "settings",label: "Settings",     icon: Cog },
                ].map((t) => {
                    const I = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`legal-search-tab-${t.key}`}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition ${
                                active
                                    ? "border-indigo-700 text-indigo-700 dark:text-indigo-300"
                                    : "border-transparent text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
                            }`}
                        >
                            <I className="w-4 h-4" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "search"   && <SearchPanel settings={settings} />}
            {tab === "mine"     && <LibraryPanel scope="mine" />}
            {tab === "firm"     && <LibraryPanel scope="firm" />}
            {tab === "settings" && <SettingsPanel value={settings} onChange={setSettings} />}
        </div>
    );
}

// ─────────────────────────── Search panel ────────────────────────────────
const EMPTY_FILTERS = {
    state_code: "",
    case_type: "",
    court_types: [],
    statuses: [],
    sort: "recent",
};

function SearchPanel({ settings }) {
    const [form, setForm] = useState({ query: "", search_in: "any", year: "", source: "ik" });
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [ikFilters, setIkFilters] = useState({
        doctypes: "", fromdate: "", todate: "", title: "", cite: "", author: "", bench: "",
    });
    const [showFilters, setShowFilters] = useState(false);
    const [lookups, setLookups] = useState(null);
    const [results, setResults] = useState([]);
    const [meta, setMeta]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [active, setActive]   = useState(null);

    useEffect(() => {
        api.get("/legal-search/filters/lookups").then((r) => setLookups(r.data)).catch(() => {});
    }, []);

    const activeFilterCount = useMemo(() => {
        let n = 0;
        if (filters.state_code) n++;
        if (filters.case_type)  n++;
        if (filters.court_types?.length) n += filters.court_types.length;
        if (filters.statuses?.length)    n += filters.statuses.length;
        if (filters.sort && filters.sort !== "recent") n++;
        if (form.year) n++;
        // IK-specific
        Object.values(ikFilters).forEach((v) => { if (v) n++; });
        return n;
    }, [filters, form.year, ikFilters]);

    const runSearch = async () => {
        if (!form.query.trim()) {
            toast.error("Please enter a search term.");
            return;
        }
        setLoading(true);
        try {
            const body = {
                query: form.query.trim(),
                source: form.source,
                search_in: form.search_in,
                page: 1,
                sort: filters.sort || "recent",
            };
            if (form.year)            body.year       = parseInt(form.year);
            if (filters.state_code)   body.state_code = filters.state_code;
            if (filters.case_type)    body.case_type  = filters.case_type;
            if (filters.court_types?.length) body.court_types = filters.court_types;
            if (filters.statuses?.length)    body.statuses    = filters.statuses;
            // IK filters (sent when source = ik or both)
            if (form.source === "ik" || form.source === "both") {
                if (ikFilters.doctypes) body.ik_doctypes = ikFilters.doctypes;
                if (ikFilters.fromdate) body.ik_fromdate = ikFilters.fromdate;
                if (ikFilters.todate)   body.ik_todate   = ikFilters.todate;
                if (ikFilters.title)    body.ik_title    = ikFilters.title;
                if (ikFilters.cite)     body.ik_cite     = ikFilters.cite;
                if (ikFilters.author)   body.ik_author   = ikFilters.author;
                if (ikFilters.bench)    body.ik_bench    = ikFilters.bench;
                body.ik_with_fragments = false;
                body.ik_fragment_cap = 0;
            }
            const r = await api.post("/legal-search/search", body);
            setResults(r.data.results || []);
            setMeta({ total: r.data.total, cost: r.data.cost });
            if ((r.data.results || []).length === 0) {
                toast.info("No matching cases found. Try a broader query.");
            }
        } catch (e) {
            toast.error(apiError(e, "Search failed."));
        } finally {
            setLoading(false);
        }
    };

    const onSearch = (e) => { e?.preventDefault?.(); runSearch(); };

    const clearOneFilter = (k, v) => {
        setFilters((prev) => {
            const n = { ...prev };
            if (k === "court_types" || k === "statuses") {
                n[k] = (prev[k] || []).filter((x) => x !== v);
            } else {
                n[k] = "";
            }
            return n;
        });
    };

    return (
        <div className="space-y-4">
            {/* Source toggle */}
            <div className="flex flex-wrap items-center gap-2" data-testid="legal-search-source-toggle">
                <span className="text-xs uppercase tracking-wider text-zinc-500 mr-1">Source:</span>
                {[
                    { v: "ecourts", label: "eCourts", hint: "Real-time court records" },
                    { v: "ik",      label: "Indian Kanoon", hint: "40+ years of historical judgments" },
                    { v: "both",    label: "Both", hint: "Parallel · ~₹2 per search" },
                ].map((s) => (
                    <button
                        key={s.v}
                        onClick={() => setForm({ ...form, source: s.v })}
                        data-testid={`legal-search-source-${s.v}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                            form.source === s.v
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-zinc-300 dark:border-zinc-700 hover:border-primary text-zinc-700 dark:text-zinc-300"
                        }`}
                        title={s.hint}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <form onSubmit={onSearch} className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-4 grid lg:grid-cols-12 gap-3 items-end">
                <div className="lg:col-span-5">
                    <Field label="Search query">
                        <Input
                            data-testid="legal-search-query"
                            value={form.query}
                            onChange={(e) => setForm({ ...form, query: e.target.value })}
                            placeholder="e.g. Vishaka, BP Saseendran, Justice Chandrachud, Sec 138 NI Act"
                        />
                    </Field>
                </div>
                <div className="lg:col-span-3">
                    <Field label="Search in">
                        <Select
                            value={form.search_in}
                            onChange={(e) => setForm({ ...form, search_in: e.target.value })}
                            options={SCOPE_OPTIONS}
                        />
                    </Field>
                </div>
                <div className="lg:col-span-2">
                    <Field label="Year">
                        <Input
                            data-testid="legal-search-year"
                            value={form.year}
                            onChange={(e) => setForm({ ...form, year: e.target.value.replace(/[^\d]/g,"") })}
                            placeholder="2024"
                            maxLength={4}
                        />
                    </Field>
                </div>
                <div className="lg:col-span-2 flex gap-2">
                    <button
                        type="button"
                        onClick={() => setShowFilters(true)}
                        data-testid="legal-search-open-filters"
                        className="relative flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-zinc-300 dark:border-zinc-700 hover:border-primary text-sm transition"
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                        Filters
                        {activeFilterCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>
                    <GoldButton type="submit" disabled={loading} data-testid="legal-search-submit"
                                className="flex-1 justify-center">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        Search
                    </GoldButton>
                </div>
            </form>

            {/* Active filter chips */}
            {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="legal-search-filter-chips">
                    {form.year && <FilterChip label={`Year: ${form.year}`} onClear={() => setForm({...form, year: ""})} />}
                    {filters.state_code && <FilterChip label={`State: ${filters.state_code}`} onClear={() => clearOneFilter("state_code")} />}
                    {filters.case_type && <FilterChip label={`Case Type: ${filters.case_type}`} onClear={() => clearOneFilter("case_type")} />}
                    {(filters.court_types || []).map((ct) => (
                        <FilterChip key={ct} label={`Court: ${ct}`} onClear={() => clearOneFilter("court_types", ct)} />
                    ))}
                    {(filters.statuses || []).map((s) => (
                        <FilterChip key={s} label={`Status: ${s}`} onClear={() => clearOneFilter("statuses", s)} />
                    ))}
                    {filters.sort && filters.sort !== "recent" && (
                        <FilterChip label={`Sort: ${filters.sort}`} onClear={() => setFilters({...filters, sort: "recent"})} />
                    )}
                    <button onClick={() => { setFilters(EMPTY_FILTERS); setForm({...form, year: ""}); }}
                            className="text-xs text-zinc-500 hover:text-rose-600 underline ml-1"
                            data-testid="legal-search-clear-filters">
                        Clear all
                    </button>
                </div>
            )}

            {meta && (
                <div className="text-xs text-zinc-500" data-testid="legal-search-meta">
                    {meta.total} matches · billed ₹{meta.cost.billed_inr.toFixed(2)} for this search
                </div>
            )}

            <div className="space-y-3">
                {/* Indian Kanoon attribution — required by their API ToS
                    whenever IK search results / documents are displayed.
                    Must appear on TOP of the results list, on every page
                    that surfaces IK data. Renders only when there's
                    something to attribute. */}
                {results.length > 0 && <PoweredByIKanoonBadge placement="top"/>}
                {results.map((r) => (
                    <ResultCard key={r.cnr} r={r} onHeadnote={() => setActive(r)} />
                ))}
                {!loading && results.length === 0 && (
                    <EmptyState
                        title="Start searching"
                        subtitle="Search live Indian court records and generate IRAC headnotes with a single click."
                    />
                )}
                {/* Mandatory AS-IS disclaimer pass-through (IK ToS §
                    "Liability Limitations"). Surfaces the upstream
                    sourcing + accuracy disclaimer once per page. */}
                {results.length > 0 && (
                    <p className="text-[10px] text-zinc-500 leading-relaxed" data-testid="ikanoon-as-is-notice">
                        Results sourced from public Indian court records via Indian Kanoon and provided
                        "as is" without warranty of accuracy, completeness, or fitness for any particular
                        purpose. Always verify against the official certified copy before relying on
                        any precedent in court.
                    </p>
                )}
            </div>

            {showFilters && (
                <FilterDrawer
                    source={form.source}
                    lookups={lookups}
                    filters={filters}
                    ikFilters={ikFilters}
                    onChange={setFilters}
                    onIkChange={setIkFilters}
                    onClose={() => setShowFilters(false)}
                    onApply={() => { setShowFilters(false); if (form.query.trim()) runSearch(); }}
                />
            )}

            {active && (
                <HeadnoteModal
                    result={active}
                    settings={settings}
                    onClose={() => setActive(null)}
                />
            )}
        </div>
    );
}

// ─────────────────────────── FilterChip ────────────────────────────
function FilterChip({ label, onClear }) {
    return (
        <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
            {label}
            <button onClick={onClear} className="ml-0.5 hover:text-rose-600" aria-label="Clear">
                <X className="w-3 h-3" />
            </button>
        </span>
    );
}

// ─────────────────────────── FilterDrawer ──────────────────────────
function FilterDrawer({ source, lookups, filters, ikFilters, onChange, onIkChange, onClose, onApply }) {
    const [local, setLocal] = useState(filters);
    const [localIk, setLocalIk] = useState(ikFilters || {});
    useEffect(() => { setLocal(filters); }, [filters]);
    useEffect(() => { setLocalIk(ikFilters || {}); }, [ikFilters]);

    const toggleArr = (key, val) => {
        setLocal((prev) => {
            const cur = new Set(prev[key] || []);
            if (cur.has(val)) cur.delete(val); else cur.add(val);
            return { ...prev, [key]: Array.from(cur) };
        });
    };

    const apply = () => { onChange(local); onIkChange?.(localIk); onApply?.(); };

    const stateOptions = (lookups?.states || []).map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }));
    const caseTypeOptions = (lookups?.case_types || []).map((c) => ({ value: c.value, label: c.label }));
    const sortOptions = (lookups?.sort || [
        { value: "recent", label: "Most recent first" },
        { value: "oldest", label: "Oldest first" },
        { value: "court",  label: "By court name" },
    ]);

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="legal-search-filters-drawer">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative ml-auto w-full max-w-md h-full bg-white dark:bg-zinc-900 shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-800">
                    <div className="flex items-center gap-2">
                        <SlidersHorizontal className="w-4 h-4 text-indigo-700 dark:text-indigo-300" />
                        <div className="font-semibold text-zinc-900 dark:text-zinc-100">All Filters</div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800"
                            data-testid="legal-search-filters-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {/* eCourts filters — visible for ecourts + both */}
                    {(source === "ecourts" || source === "both") && (
                    <FilterSection title="Court type" hint="Filter results by court hierarchy.">
                        <div className="flex flex-wrap gap-2">
                            {(lookups?.court_types || []).map((c) => {
                                const on = (local.court_types || []).includes(c.value);
                                return (
                                    <button key={c.value}
                                        onClick={() => toggleArr("court_types", c.value)}
                                        data-testid={`filter-court-${c.value}`}
                                        className={`text-xs px-3 py-1.5 rounded-full border transition ${
                                            on
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "border-zinc-300 dark:border-zinc-700 hover:border-primary"
                                        }`}>
                                        {c.label}
                                    </button>
                                );
                            })}
                        </div>
                    </FilterSection>
                    )}

                    {(source === "ecourts" || source === "both") && (
                    <FilterSection title="Case status">
                        <div className="flex flex-wrap gap-2">
                            {(lookups?.statuses || []).map((s) => {
                                const on = (local.statuses || []).includes(s.value);
                                return (
                                    <button key={s.value}
                                        onClick={() => toggleArr("statuses", s.value)}
                                        data-testid={`filter-status-${s.value}`}
                                        className={`text-xs px-3 py-1.5 rounded-full border transition ${
                                            on
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "border-zinc-300 dark:border-zinc-700 hover:border-primary"
                                        }`}>
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                    </FilterSection>
                    )}

                    {(source === "ecourts" || source === "both") && (
                    <FilterSection title="State">
                        {stateOptions.length > 0 ? (
                            <Select
                                value={local.state_code}
                                onChange={(e) => setLocal({ ...local, state_code: e.target.value })}
                                placeholder="All states"
                                options={[{ value: "", label: "All states" }, ...stateOptions]}
                            />
                        ) : (
                            <Input
                                value={local.state_code}
                                onChange={(e) => setLocal({ ...local, state_code: e.target.value.toUpperCase() })}
                                placeholder="e.g. KL, MH, DL"
                                maxLength={3}
                            />
                        )}
                    </FilterSection>
                    )}

                    {(source === "ecourts" || source === "both") && (
                    <FilterSection title="Case type">
                        <Select
                            value={local.case_type}
                            onChange={(e) => setLocal({ ...local, case_type: e.target.value })}
                            placeholder="Any case type"
                            options={[{ value: "", label: "Any case type" }, ...caseTypeOptions]}
                        />
                    </FilterSection>
                    )}

                    {/* Indian Kanoon filters */}
                    {(source === "ik" || source === "both") && (
                        <div className="space-y-5 pt-2 border-t border-zinc-200 dark:border-zinc-800">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 pt-2">
                                Indian Kanoon — advanced filters
                            </div>

                            <FilterSection title="Court / Tribunal" hint="Comma-separated IK doctypes — see indiankanoon docs.">
                                <Input
                                    value={localIk.doctypes || ""}
                                    onChange={(e) => setLocalIk({ ...localIk, doctypes: e.target.value })}
                                    placeholder="e.g. supremecourt, delhi, bombay, tribunals"
                                    data-testid="ik-filter-doctypes"
                                />
                            </FilterSection>

                            <FilterSection title="Title contains">
                                <Input
                                    value={localIk.title || ""}
                                    onChange={(e) => setLocalIk({ ...localIk, title: e.target.value })}
                                    placeholder="e.g. Kesavananda"
                                    data-testid="ik-filter-title"
                                />
                            </FilterSection>

                            <FilterSection title="Citation">
                                <Input
                                    value={localIk.cite || ""}
                                    onChange={(e) => setLocalIk({ ...localIk, cite: e.target.value })}
                                    placeholder="e.g. 1993 AIR"
                                    data-testid="ik-filter-cite"
                                />
                            </FilterSection>

                            <FilterSection title="Author judge">
                                <Input
                                    value={localIk.author || ""}
                                    onChange={(e) => setLocalIk({ ...localIk, author: e.target.value })}
                                    placeholder="e.g. arijit pasayat"
                                    data-testid="ik-filter-author"
                                />
                            </FilterSection>

                            <FilterSection title="Bench includes">
                                <Input
                                    value={localIk.bench || ""}
                                    onChange={(e) => setLocalIk({ ...localIk, bench: e.target.value })}
                                    placeholder="e.g. chandrachud"
                                    data-testid="ik-filter-bench"
                                />
                            </FilterSection>

                            <FilterSection title="Date range" hint="Format: DD-MM-YYYY">
                                <div className="grid grid-cols-2 gap-2">
                                    <Input
                                        value={localIk.fromdate || ""}
                                        onChange={(e) => setLocalIk({ ...localIk, fromdate: e.target.value })}
                                        placeholder="From: 01-01-2020"
                                        data-testid="ik-filter-fromdate"
                                    />
                                    <Input
                                        value={localIk.todate || ""}
                                        onChange={(e) => setLocalIk({ ...localIk, todate: e.target.value })}
                                        placeholder="To: 31-12-2024"
                                        data-testid="ik-filter-todate"
                                    />
                                </div>
                            </FilterSection>
                        </div>
                    )}

                    <FilterSection title="Sort by">
                        <Select
                            value={local.sort || "recent"}
                            onChange={(e) => setLocal({ ...local, sort: e.target.value })}
                            options={sortOptions}
                        />
                    </FilterSection>
                </div>

                <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-800">
                    <button onClick={() => { setLocal(EMPTY_FILTERS); }}
                            className="text-sm text-zinc-500 hover:text-rose-600 underline"
                            data-testid="legal-search-filters-reset">
                        Reset all
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
                        <GoldButton onClick={apply} data-testid="legal-search-filters-apply">
                            Apply filters
                        </GoldButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FilterSection({ title, hint, children }) {
    return (
        <div className="space-y-2">
            <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300">{title}</div>
                {hint && <div className="text-[10px] text-zinc-500 mt-0.5">{hint}</div>}
            </div>
            {children}
        </div>
    );
}

// Helper — downloads the original court PDF for an IK or eCourts result.
// Streams via our backend so wallet/passbook is debited correctly.
async function downloadOriginalOrder(r) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    let url, filename;
    if (r.source === "ik" && r.docid) {
        url = `${base}/api/indiankanoon/origdoc/${r.docid}/download`;
        filename = `court-order-${r.docid}.pdf`;
    } else if (r.cnr) {
        // eCourts: download the most recent order via the existing endpoint
        url = `${base}/api/ecourts/cases/${encodeURIComponent(r.cnr)}/order-pdf`;
        filename = `order-${r.cnr}.pdf`;
    } else {
        toast.error("This result has no downloadable original PDF.");
        return;
    }
    const toastId = toast.loading("Fetching original court document…");
    try {
        const resp = await fetch(url, { credentials: "include" });
        if (!resp.ok) {
            const text = await resp.text();
            throw new Error(text || `HTTP ${resp.status}`);
        }
        const blob = await resp.blob();
        const mime = (resp.headers.get("content-type") || "").toLowerCase();
        const isHtml = mime.includes("html");
        const blobUrl = URL.createObjectURL(blob);
        if (isHtml) {
            window.open(blobUrl, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } else {
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
        }
        const cost = resp.headers.get("X-IK-Cost-INR");
        const costStr = cost ? ` · billed ₹${cost}` : "";
        toast.success(`Original court document fetched${costStr}`, { id: toastId });
    } catch (e) {
        const msg = (e && e.message) || "Download failed.";
        toast.error(msg.includes("NOT_FOUND")
            ? "Original PDF is not available at the source court for this judgment."
            : `Download failed: ${msg.slice(0,160)}`, { id: toastId });
    }
}

// Sanitises IK markup — keeps only basic highlight tags, strips everything else.
function safeHtml(s) {
    if (!s) return "";
    // First strip any script/style/iframe blocks entirely
    let out = String(s).replace(/<\/?(?:script|style|iframe|object|embed|link|meta)[^>]*>/gi, "");
    // Allow only b/i/em/strong/u/mark; escape every other angle bracket
    out = out.replace(/<(?!\/?(b|i|em|strong|u|mark)\b)[^>]*>/gi, "");
    return out;
}

function ResultCard({ r, onHeadnote }) {
    const isIK = r.source === "ik";
    return (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-4">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:justify-between gap-3">
                <div className="min-w-0 w-full sm:flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${
                            isIK
                                ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                : "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                        }`}>
                            {isIK ? "Indian Kanoon" : "eCourts"}
                        </span>
                        <div className="font-semibold text-zinc-900 dark:text-zinc-100 break-words min-w-0 flex-1"
                             dangerouslySetInnerHTML={{ __html: safeHtml(r.title || r.cnr || r.docid || "") }} />
                    </div>
                    <div className="text-sm text-zinc-500 mt-1 break-words">
                        {r.court_name && <span>{r.court_name}</span>}
                        {r.case_type && <span> · {r.case_type}</span>}
                        {r.filing_date && <span> · Filed {r.filing_date}</span>}
                        {r.decision_date && <span> · {r.decision_date}</span>}
                        {r.case_status && (
                            <span className="ml-2 inline-block text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                                {r.case_status}
                            </span>
                        )}
                    </div>
                    {r.cnr && (
                        <div className="text-xs text-zinc-500 mt-1 font-mono break-all">CNR · {r.cnr}</div>
                    )}
                    {r.docid && (
                        <div className="text-xs text-zinc-500 mt-1 font-mono break-all">IK · {r.docid}</div>
                    )}
                    {r.fragment_html && (
                        <div className="text-sm text-zinc-700 dark:text-zinc-300 mt-2 line-clamp-3 leading-relaxed"
                             dangerouslySetInnerHTML={{ __html: safeHtml(r.fragment_html) }} />
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    {r.headnote_saved && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="w-4 h-4" /> Saved
                        </span>
                    )}
                    <button
                        onClick={() => downloadOriginalOrder(r)}
                        title="Download original court PDF (₹1.00)"
                        data-testid={`legal-search-download-${r.cnr || r.docid}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl border border-zinc-300 dark:border-zinc-700 hover:border-primary text-sm font-medium transition flex-1 sm:flex-none"
                    >
                        <Download className="w-4 h-4" />
                        <span className="whitespace-nowrap">Original PDF</span>
                    </button>
                    <button
                        onClick={onHeadnote}
                        data-testid={`legal-search-headnote-${r.cnr || r.docid}`}
                        className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground hover:opacity-90 text-sm font-medium transition flex-1 sm:flex-none"
                    >
                        <Sparkles className="w-4 h-4" />
                        <span className="whitespace-nowrap">Generate AI Headnote</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────── Headnote modal ─────────────────────────────
function HeadnoteModal({ result, settings, onClose }) {
    const isIK = result.source === "ik";
    // For IK results we skip the order-picker entirely (initial step = "choose"
    // but with no orders to pick — we just show a confirm screen)
    const [step, setStep]       = useState(isIK ? "choose" : "loading_orders");
    const [orders, setOrders]   = useState([]);
    const [filename, setFilename] = useState(null);
    const [headnote, setHeadnote] = useState(null);
    const [visibility, setVisibility] = useState(settings?.default_visibility || "private");
    const [cases, setCases]     = useState([]);
    const [attachCaseId, setAttachCaseId] = useState("");
    // Tenant-facing price for the IK doc fetch — base + platform markup,
    // never the raw IK cost. Loaded lazily from /price-quote so admin
    // markup changes propagate without a redeploy.
    const [docPrice, setDocPrice] = useState(null);

    useEffect(() => {
        if (!isIK) return;
        api.get("/legal-search/price-quote", { params: { action: "ik_doc_fetch" } })
            .then(r => setDocPrice(Number(r.data?.billed_inr ?? 0)))
            .catch(() => setDocPrice(null));
    }, [isIK]);

    useEffect(() => {
        let cancelled = false;

        if (!isIK) {
            api.get(`/legal-search/case/${encodeURIComponent(result.cnr)}/orders`)
                .then((r) => {
                    if (cancelled) return;
                    const list = r.data.orders || [];
                    setOrders(list);
                    if (list.length === 0) {
                        toast.error("No orders published for this case yet.");
                        onClose();
                        return;
                    }
                    setFilename(list[0]?.filename || list[0]?.file || null);
                    setStep("choose");
                })
                .catch((e) => {
                    toast.error(apiError(e, "Could not list orders for this case."));
                    onClose();
                });
        }

        api.get("/cases?limit=50").then((r) => {
            if (!cancelled) setCases(r.data?.items || r.data || []);
        }).catch(() => {});

        return () => { cancelled = true; };
    }, [result.cnr, result.docid, isIK]);

    const onGenerate = async () => {
        setStep("generating");
        try {
            const url = isIK ? "/legal-search/headnote-ik" : "/legal-search/headnote";
            const body = isIK
                ? { docid: result.docid, visibility }
                : { cnr: result.cnr, filename, visibility };
            const r = await api.post(url, body);
            setHeadnote(r.data);
            setStep("done");
            toast.success("AI Headnote generated.");
            if (settings?.default_save_to_matter === "auto" && cases.length > 0) {
                setAttachCaseId(cases[0].id || cases[0]._id);
            }
        } catch (e) {
            toast.error(apiError(e, "AI Headnote generation failed."));
            setStep("choose");
        }
    };

    const onAttach = async () => {
        if (!attachCaseId || !headnote) return;
        try {
            await api.post(`/legal-search/headnotes/${headnote.id}/attach`,
                            { case_id: attachCaseId });
            // Surface a deep-link to the litigation Documents tab so the
            // user can immediately see WHERE the headnote landed instead
            // of having to hunt for it.
            toast.success("Headnote attached to the matter", {
                description: "Open the matter → Documents tab to view it.",
                action: {
                    label: "View",
                    onClick: () => { window.location.href = `/app/cases/${attachCaseId}?tab=documents`; },
                },
                duration: 8000,
            });
            onClose();
        } catch (e) {
            toast.error(apiError(e, "Could not attach headnote."));
        }
    };

    return (
        <Modal open onClose={onClose} title="AI Legal Headnote" size="lg" testId="legal-search-modal">
            <div className="space-y-4">
                {/* IK attribution — AI IRAC headnotes are derived from
                    Indian Kanoon content (RAG use). ToS requires
                    prominent attribution on every surface that displays
                    the result. */}
                <PoweredByIKanoonBadge placement="inline"/>
                <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100"
                          dangerouslySetInnerHTML={{ __html: safeHtml(result.title || result.cnr || "") }} />
                    <button
                        onClick={() => downloadOriginalOrder(result)}
                        title="Download original court PDF"
                        data-testid="legal-search-modal-download"
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-zinc-300 dark:border-zinc-700 hover:border-primary text-xs font-medium transition"
                    >
                        <Download className="w-3.5 h-3.5" />
                        Original PDF
                    </button>
                </div>
                <div className="text-xs text-zinc-500">{result.court_name} · CNR {result.cnr}</div>

                {step === "loading_orders" && (
                    <div className="flex items-center gap-2 text-sm text-zinc-500 py-6">
                        <Loader2 className="w-4 h-4 animate-spin" /> Fetching available orders…
                    </div>
                )}

                {step === "choose" && (
                    <>
                        {!isIK && (
                            <Field label="Pick an order to summarise">
                                <Select
                                    value={filename}
                                    onChange={(e) => setFilename(e.target.value)}
                                    options={orders.map((o) => ({
                                        value: o.filename || o.file,
                                        label: `${o.orderDate || o.date || "—"} · ${(o.title || o.filename || o.file || "").slice(0, 60)}`,
                                    }))}
                                />
                            </Field>
                        )}
                        {isIK && (
                            <div className="text-sm text-zinc-700 dark:text-zinc-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3" data-testid="legal-search-cost-notice">
                                <span className="font-medium">Indian Kanoon full judgment</span> will be fetched
                                {docPrice !== null && docPrice > 0
                                    ? <> for <span className="font-semibold">₹{docPrice.toFixed(2)}</span></>
                                    : null}
                                {" "}and run through your OpenAI Tier-1 to produce an IRAC headnote.
                                {" "}AI tokens are billed separately at-cost from your wallet.
                            </div>
                        )}
                        <Field label="Visibility">
                            <Select value={visibility} onChange={(e) => setVisibility(e.target.value)} options={VIS_OPTIONS} />
                        </Field>
                        <div className="text-xs text-zinc-500">
                            Tip: order text + AI tokens are billed via your AI wallet. You&apos;ll see the cost in the passbook.
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
                            <GoldButton onClick={onGenerate} data-testid="legal-search-generate-confirm">
                                <Sparkles className="w-4 h-4" /> Generate
                            </GoldButton>
                        </div>
                    </>
                )}

                {step === "generating" && (
                    <div className="flex items-center gap-2 text-sm text-zinc-500 py-10 justify-center">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating IRAC headnote… this can take 10–25 seconds.
                    </div>
                )}

                {step === "done" && headnote && (
                    <>
                        <HeadnoteView h={headnote} />
                        {settings?.default_save_to_matter !== "off" && cases.length > 0 && (
                            <div className="border-t border-zinc-200 dark:border-zinc-800 pt-3 space-y-2">
                                <Field label="Attach to a matter (saves as a Document)">
                                    <Select
                                        value={attachCaseId}
                                        onChange={(e) => setAttachCaseId(e.target.value)}
                                        placeholder="Select a case…"
                                        options={cases.map((c) => ({
                                            value: c.id || c._id,
                                            label: c.title || c.internal_number || c.cnr || c.id,
                                        }))}
                                    />
                                </Field>
                                <div className="flex justify-end gap-2">
                                    <button onClick={onClose} className="px-4 py-2 text-sm">Close</button>
                                    <GoldButton onClick={onAttach} disabled={!attachCaseId}
                                                data-testid="legal-search-attach-confirm">
                                        <Paperclip className="w-4 h-4" /> Attach to matter
                                    </GoldButton>
                                </div>
                            </div>
                        )}
                        {(settings?.default_save_to_matter === "off" || cases.length === 0) && (
                            <div className="flex justify-end pt-2">
                                <GoldButton onClick={onClose}>Done</GoldButton>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}

function HeadnoteBlock({ title, body }) {
    if (!body) return null;
    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">{title}</div>
            <div className="text-sm text-zinc-800 dark:text-zinc-200 mt-1 whitespace-pre-wrap">{body}</div>
        </div>
    );
}

function HeadnoteView({ h }) {
    return (
        <div className="space-y-3 bg-zinc-50 dark:bg-zinc-900/60 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
            {h.summary && (
                <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{h.summary}</div>
            )}
            <HeadnoteBlock title="Issue"       body={h.issue} />
            <HeadnoteBlock title="Rule"        body={h.rule} />
            <HeadnoteBlock title="Application" body={h.application} />
            <HeadnoteBlock title="Conclusion"  body={h.conclusion} />
            {(h.citations?.length > 0) && (
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">Citations</div>
                    <ul className="text-sm text-zinc-800 dark:text-zinc-200 mt-1 list-disc pl-5">
                        {h.citations.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                </div>
            )}
            {(h.keywords?.length > 0) && (
                <div className="flex flex-wrap gap-1.5">
                    {h.keywords.map((k, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                            <Hash className="w-3 h-3" />{k}
                        </span>
                    ))}
                </div>
            )}
            <div className="text-[10px] text-zinc-500 pt-1">
                Generated by {h.provider}/{h.model} · {h.created_at?.replace("T", " ").slice(0, 16)}
            </div>
        </div>
    );
}

// ─────────────────────────── Library panels ─────────────────────────────
function LibraryPanel({ scope }) {
    const [rows, setRows] = useState([]);
    const [q, setQ]       = useState("");
    const [loading, setLoading] = useState(true);
    const [active, setActive]   = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/legal-search/headnotes",
                { params: { scope, q: q || undefined, limit: 100 } });
            setRows(r.data || []);
        } catch (e) {
            toast.error(apiError(e, "Failed to load headnotes."));
        } finally {
            setLoading(false);
        }
    }, [scope, q]);

    useEffect(() => { load(); }, [load]);

    const onToggleVisibility = async (h) => {
        const v = h.visibility === "firm" ? "private" : "firm";
        try {
            await api.patch(`/legal-search/headnotes/${h.id}/visibility`,
                            { visibility: v });
            toast.success(`Now ${v === "firm" ? "shared with firm" : "private"}.`);
            load();
        } catch (e) {
            toast.error(apiError(e, "Could not update visibility."));
        }
    };

    const onDelete = async (h) => {
        if (!window.confirm("Delete this headnote? This cannot be undone."))
            return;
        try {
            await api.delete(`/legal-search/headnotes/${h.id}`);
            toast.success("Headnote deleted.");
            load();
        } catch (e) {
            toast.error(apiError(e, "Delete failed."));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[200px]">
                    <Input
                        data-testid="legal-library-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Filter by case, citation, or keyword…"
                    />
                </div>
                <GoldButton onClick={load}>Refresh</GoldButton>
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-sm text-zinc-500 py-6">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
            )}

            {!loading && rows.length === 0 && (
                <EmptyState
                    title={scope === "mine" ? "No saved headnotes yet" : "Firm library is empty"}
                    subtitle={scope === "mine"
                        ? "Use the Search tab to find a case and generate your first headnote."
                        : "When colleagues share their AI headnotes, they'll appear here."}
                />
            )}

            <div className="grid md:grid-cols-2 gap-3">
                {rows.map((h) => (
                    <div key={h.id} className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-4 hover:shadow-md transition"
                         data-testid={`legal-library-row-${h.id}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setActive(h)}>
                                <div className="font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                                    {h.case_title || h.cnr}
                                </div>
                                <div className="text-xs text-zinc-500 mt-1">
                                    {h.court_name} · {h.order_date || "—"}
                                </div>
                                {h.summary && (
                                    <div className="text-sm text-zinc-700 dark:text-zinc-300 mt-2 line-clamp-3">
                                        {h.summary}
                                    </div>
                                )}
                                {(h.keywords?.length > 0) && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {h.keywords.slice(0, 5).map((k, i) => (
                                            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => onToggleVisibility(h)}
                                    title={h.visibility === "firm" ? "Shared with firm — click to make private" : "Private — click to share with firm"}
                                    className="p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                    data-testid={`legal-library-visibility-${h.id}`}
                                >
                                    {h.visibility === "firm"
                                        ? <Globe2 className="w-4 h-4 text-indigo-600" />
                                        : <Lock  className="w-4 h-4 text-zinc-500" />}
                                </button>
                                <button
                                    onClick={() => onDelete(h)}
                                    title="Delete"
                                    className="p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/30"
                                    data-testid={`legal-library-delete-${h.id}`}
                                >
                                    <Trash2 className="w-4 h-4 text-rose-600" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {active && (
                <Modal open onClose={() => setActive(null)}
                       title={active.case_title || active.cnr} size="lg" testId="legal-library-view">
                    <div className="flex justify-end mb-3">
                        <button
                            onClick={() => downloadOriginalOrder({
                                source: active.source || (active.ik_docid ? "ik" : "ecourts"),
                                docid: active.ik_docid,
                                cnr: active.cnr,
                            })}
                            data-testid="library-modal-download"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-zinc-300 dark:border-zinc-700 hover:border-primary text-xs font-medium transition"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Download original PDF
                        </button>
                    </div>
                    <HeadnoteView h={active} />
                </Modal>
            )}
        </div>
    );
}

// ─────────────────────────── Settings panel ─────────────────────────────
function SettingsPanel({ value, onChange }) {
    const [s, setS] = useState(value || { default_visibility: "private",
                                          default_save_to_matter: "manual",
                                          ai_model: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (value) setS({ ...value, ai_model: value.ai_model || "" }); }, [value]);

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                default_visibility: s.default_visibility,
                default_save_to_matter: s.default_save_to_matter,
                ai_model: s.ai_model || null,
                preferred_headnote_source: s.preferred_headnote_source || "ik",
            };
            const r = await api.patch("/legal-search/settings", body);
            onChange?.(r.data);
            toast.success("Legal Search settings saved.");
        } catch (e) {
            toast.error(apiError(e, "Could not save settings."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-2xl bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 space-y-4"
             data-testid="legal-search-settings">
            <Field label="Default visibility for new headnotes" hint="Each headnote can still be re-shared from the library.">
                <Select value={s.default_visibility}
                        onChange={(e) => setS({ ...s, default_visibility: e.target.value })}
                        options={VIS_OPTIONS} />
            </Field>
            <Field label="Save to matter behaviour" hint="Controls the post-generation 'Attach to matter' prompt.">
                <Select value={s.default_save_to_matter}
                        onChange={(e) => setS({ ...s, default_save_to_matter: e.target.value })}
                        options={ATTACH_OPTIONS} />
            </Field>
            <Field label="AI model override (optional)" hint="Leave empty to use the platform default (your Tier-1 OpenAI). Examples: gpt-4o, gpt-4o-mini.">
                <Input value={s.ai_model}
                       onChange={(e) => setS({ ...s, ai_model: e.target.value })}
                       placeholder="gpt-4o" />
            </Field>
            <Field label="Preferred AI Headnote source" hint="When a case is available from both providers, which source feeds OpenAI?">
                <Select value={s.preferred_headnote_source || "ik"}
                        onChange={(e) => setS({ ...s, preferred_headnote_source: e.target.value })}
                        options={[
                            { value: "ik",      label: "Indian Kanoon (cheaper, richer judgment text)" },
                            { value: "ecourts", label: "eCourts (live, but partial order text)" },
                        ]} />
            </Field>
            <div className="flex justify-end">
                <GoldButton onClick={save} disabled={saving} data-testid="legal-search-settings-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Save
                </GoldButton>
            </div>
        </div>
    );
}
